import { useEffect } from 'react';

import useScrollToNextField from '../hooks/useScrollToNextField';
import UserIcon from '@/assets/user.svg?react';

import { useFormDataQuery } from '@/api/getters';
import { useFormMutation, useSubmitStagesMutation } from '@/api/setters';
import CvIcon from '@/assets/cv.svg?react';
import HomeIcon from '@/assets/home.svg?react';
import PadlockIcon from '@/assets/padlock.svg?react';
import TitledCard from '@/components/ui/TitledCard';
import { formMutationDto } from '@/dto/formMutationDto';
import { useStepper } from '@/features/v2/lib/stepper';
import { scrollToTop } from '@/helpers/scrollToTop';

import CompanyInvoiceSection from '../components/CompanyInvoiceSection';
import { EcommerceStepForm } from '../components/EcommerceStepForm';
import InputApartmentNumber from '../components/InputApartmentNumber';
import InputBillingEmail from '../components/InputBillingEmail';
import InputBillingFirstName from '../components/InputBillingFirstName';
import InputBillingLastName from '../components/InputBillingLastName';
import InputBillingPhone from '../components/InputBillingPhone';
import InputBuildingNumber from '../components/InputBuildingNumber';
import InputCity from '../components/InputCity';
import InputPostalCode from '../components/InputPostalCode';
import InputStreet from '../components/InputStreet';
import RadioGroupBuyerType, {
  IS_COMPANY_NAME,
} from '../components/RadioGroupBuyerType';
import autofillBillingData from '../helpers/autofillBillingData';
import {
  ADDON_PRICES,
  DOCUMENT_PRICES,
  type EcommerceFormValues,
} from '../schema';
import { BILLING_STEP } from '../stepsSchema';
import Step5Summary from './Step5Summary';
import { useLingui } from '@lingui/react';
import { useFormContext } from 'react-hook-form';

const Step4BillingData = () => {
  const { i18n } = useLingui();
  const { getValues, setValue, trigger, watch } =
    useFormContext<EcommerceFormValues>();
  const { step, innerStep, innerObject, nextInnerStep, currentStep } =
    useStepper();
  const { data } = useFormDataQuery();
  const formMutation = useFormMutation();
  const submitStages = useSubmitStagesMutation();

  const { lastStep } = BILLING_STEP.lastStep(watch());
  const isSummaryScreen = innerStep >= lastStep;

  // FormComponent mounts every step at once and hides inactive ones with CSS
  // (wizard.tsx), so a mount-only effect would fire before the user ever
  // reaches this step. Key it off the active schema entry so it fires on step
  // entry instead.
  const isActiveStep = currentStep?.name === BILLING_STEP.name;

  useEffect(() => {
    if (!isActiveStep) return;
    autofillBillingData({ getValues, setValue });
  }, [isActiveStep, getValues, setValue]);

  const { ref: nextFieldRef } = useScrollToNextField(watch(), [
    IS_COMPANY_NAME,
  ]);

  const handleNext = async () => {
    const valid = await trigger(BILLING_STEP.fields(innerStep));
    if (!valid) return;
    scrollToTop();
    nextInnerStep();
  };

  const handlePay = async () => {
    // On the summary screen the schema's field list also covers payment_method.
    const valid = await trigger(BILLING_STEP.fields(innerStep));
    if (!valid || !data) return;

    await formMutation.mutateAsync({
      ...formMutationDto({
        form_data: getValues(),
        stepper: { step, innerStep: innerObject },
      }),
    });
    await submitStages.mutateAsync(data.stage);
  };

  if (isSummaryScreen) {
    const values = watch();
    const orderTotal =
      (values.document_type ? DOCUMENT_PRICES[values.document_type] ?? 0 : 0) +
      (values.addons ?? []).reduce(
        (sum, addon) => sum + (ADDON_PRICES[addon] ?? 0),
        0
      );

    return (
      <EcommerceStepForm
        backButton={!!BILLING_STEP.backButton?.(innerStep)}
        prevButton={{ label: i18n.t('Wróć do danych') }}
        isLastStep
        submitButton={{
          label: i18n.t('Zapłać {orderTotal} zł', { orderTotal }),
          onClick: handlePay,
          isLoading: formMutation.isPending || submitStages.isPending,
        }}
      >
        <Step5Summary />
      </EcommerceStepForm>
    );
  }

  return (
    <EcommerceStepForm
      backButton={!!BILLING_STEP.backButton?.(innerStep)}
      nextButton={{
        label: i18n.t('Przejdź do podsumowania'),
        handleSubmit: handleNext,
      }}
      buttonsRef={nextFieldRef}
    >
      <TitledCard
        title={i18n.t('Dane do zamówienia')}
        className="p-[14px] md:p-6"
      >
        <div className="flex flex-col gap-3">
          {/* Dane kupującego */}
          <div className="flex flex-col gap-3 rounded-md bg-gray-200 p-[14px] md:p-6">
            <div className="flex items-center gap-2">
              <UserIcon width={16} className="shrink-0 text-primary" />
              <p className="h5 text-dark-gray-500">
                {i18n.t('Dane kupującego')}
              </p>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <InputBillingFirstName />
              <InputBillingLastName />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <InputBillingPhone />
              <InputBillingEmail />
            </div>
          </div>

          {/* Adres zamieszkania */}
          <div className="flex flex-col gap-3 rounded-md bg-gray-200 p-[14px] md:p-6">
            <div className="flex items-center gap-2">
              <HomeIcon width={16} className="shrink-0 text-primary" />
              <p className="h5 text-dark-gray-500">
                {i18n.t('Adres zamieszkania')}
              </p>
            </div>

            <InputStreet />

            <div className="grid grid-cols-2 gap-3">
              <InputBuildingNumber />
              <InputApartmentNumber />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <InputPostalCode />
              <InputCity />
            </div>
          </div>

          {/* Dla kogo jest ten zakup? + Dane do faktury (combined) */}
          <div className="flex flex-col gap-5 rounded-md bg-gray-200 p-[14px] md:p-6">
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <CvIcon width={16} className="shrink-0 text-primary" />
                <p className="h5 text-dark-gray-500">
                  {i18n.t('Dla kogo jest ten zakup?')}
                </p>
              </div>

              <RadioGroupBuyerType />
            </div>

            <CompanyInvoiceSection />
          </div>

          {/* Nota */}
          <div className="flex items-center gap-2 rounded-md bg-gray-200 px-4 py-3 md:px-6">
            <PadlockIcon width={16} className="shrink-0 text-blue-600" />
            <p className="caption-large text-oxford-blue-300">
              {i18n.t(
                'Te dane są potrzebne do obsługi zamówienia i kontaktu w sprawie dokumentu.'
              )}
            </p>
          </div>
        </div>
      </TitledCard>
    </EcommerceStepForm>
  );
};

export default Step4BillingData;
